import React, { useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { View, Text, Pressable, ActivityIndicator } from '../../lib/react-native-compat';
import { userNameAtom } from '../../store/company';
import { uploadFieldReportPhoto, deleteFieldReportPhoto } from '../../db/company';
import { showToast } from '../../helpers/toast';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import Lightbox from './Lightbox';
import { downscale } from './PhotosStep';

// Sub document uploads on the Labor & Equipment step: daily time tickets and
// weekly progress reports from subcontractors — a PDF export or a photo of the
// paper ticket. Rides the photo pipeline (same blob container, same table,
// same delete endpoint); DocType keeps them out of the Photos step, and the
// Location column carries the original file name for docs.
//
// Files upload immediately on pick (no second Upload tap); the doc type and
// caption stay editable afterwards via the photoMeta section (no update-photo
// API — the display layer merges the edits over the uploaded values).

export const DOC_KINDS = [
    { key: 'timeTicket', label: 'Daily Time Ticket', tag: 'TT' },
    { key: 'progressReport', label: 'Weekly Progress Report', tag: 'WPR' },
    { key: 'other', label: 'Other', tag: 'OTH' },
];
const kindByKey = Object.fromEntries(DOC_KINDS.map((k) => [k.key, k]));

const inputBase = {
    minHeight: 40,
    fontSize: 14,
    borderRadius: 8,
    border: '1px solid #CBD5E1',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 12,
    paddingRight: 12,
    width: '100%',
    backgroundColor: '#fff',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    outline: 'none',
};

const KindTag = ({ docType }) => (
    <View style={{ border: '1px solid #94A3B8', borderRadius: 4, paddingTop: 1, paddingBottom: 1, paddingLeft: 5, paddingRight: 5, alignSelf: 'flex-start' }}>
        <Text style={{ fontSize: 10, fontWeight: '700', color: '#475569', letterSpacing: 0.5 }}>
            {kindByKey[docType]?.tag || 'DOC'}
        </Text>
    </View>
);

const Thumb = ({ src, isPdf }) => (
    src && !isPdf ? (
        <img src={src} alt="" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, flexShrink: 0, display: 'block' }} />
    ) : (
        <View style={{ width: 96, height: 96, borderRadius: 8, backgroundColor: '#F1F5F9', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Text style={{ fontSize: 11, fontWeight: '700', color: '#64748B' }}>{isPdf ? 'PDF' : 'DOC'}</Text>
        </View>
    )
);

// totals: labor headcount stat items ([{label, value}]) — the upload trigger
// renders inside the same stat bar so sub tickets sit next to the counts.
const SubDocsUpload = ({ readOnly, totals }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const [userName] = useAtom(userNameAtom);
    const fileRef = useRef(null);
    // pending: [{ id, file, previewUrl, docType, uploading, failed }]
    const [pending, setPending] = useState([]);
    // lightbox: { src, title } — image docs open in-page instead of a new tab.
    const [lightbox, setLightbox] = useState(null);

    if (!report) return null;

    const docs = (report.photos || []).filter((p) => p.docType && p.docType !== 'photo');

    // Caption / doc-type edits after upload live on the photoMeta section
    // (same pattern the Review page uses for photo captions).
    const metaRows = report.sections?.photoMeta || [];
    const metaOf = (id) => metaRows.find((m) => m.photoId === id) || {};
    const setMeta = (id, patch) => {
        const next = metaRows.filter((m) => m.photoId !== id);
        next.push({ ...metaOf(id), photoId: id, ...patch });
        setReport((r) => ({ ...r, sections: { ...r.sections, photoMeta: next } }));
        setDirty(true);
    };

    const setPendingField = (id, field, value) =>
        setPending((p) => p.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

    const uploadOne = async (item) => {
        setPendingField(item.id, 'uploading', true);
        setPendingField(item.id, 'failed', false);
        try {
            const isPdf = item.file.type === 'application/pdf';
            const blob = isPdf ? item.file : await downscale(item.file);
            const res = await uploadFieldReportPhoto({
                reportId: report.reportId,
                buId: report.buId,
                caption: '',
                location: item.file.name, // docs: Location = original file name
                uploadedBy: userName,
                contentType: isPdf ? 'application/pdf' : 'image/jpeg',
                blob,
                docType: item.docType,
            });
            if (!res?.PhotoID) throw new Error(res?.error || 'Upload failed');
            setPending((p) => p.filter((x) => x.id !== item.id));
            setReport((r) => ({
                ...r,
                photos: [...(r.photos || []), {
                    photoId: res.PhotoID,
                    sasUrl: res.sasUrl || item.previewUrl,
                    docType: item.docType,
                    contentType: isPdf ? 'application/pdf' : 'image/jpeg',
                    caption: '',
                    location: item.file.name,
                }],
            }));
        } catch (err) {
            setPending((p) => p.map((x) => (x.id === item.id ? { ...x, uploading: false, failed: true } : x)));
            showToast('Upload failed — check connection and tap Retry.', 'error');
        }
    };

    // Picked files upload right away — no second tap.
    const onPick = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        const items = files.map((file) => ({
            id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
            file,
            previewUrl: file.type === 'application/pdf' ? null : URL.createObjectURL(file),
            docType: 'timeTicket',
            uploading: true,
            failed: false,
        }));
        setPending((p) => [...p, ...items]);
        items.forEach((item) => uploadOne(item));
    };

    const deleteDoc = async (doc) => {
        try {
            await deleteFieldReportPhoto({ photoId: doc.photoId, updatedBy: userName });
            setReport((r) => ({ ...r, photos: (r.photos || []).filter((p) => p.photoId !== doc.photoId) }));
        } catch (err) {
            showToast('Delete failed — try again.', 'error');
        }
    };

    const kindToggle = (activeKey, onPickKind, disabled) => (
        <View style={{ flexDirection: 'row', border: '1px solid #CBD5E1', borderRadius: 8, overflow: 'hidden', alignSelf: 'flex-start' }}>
            {DOC_KINDS.map((k) => (
                <Pressable
                    key={k.key}
                    onPress={() => !disabled && onPickKind(k.key)}
                    style={{ paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10, backgroundColor: activeKey === k.key ? '#1D4ED8' : '#fff', cursor: disabled ? 'default' : 'pointer' }}
                >
                    <Text style={{ fontSize: 12, fontWeight: '600', color: activeKey === k.key ? '#fff' : '#475569' }}>{k.label}</Text>
                </Pressable>
            ))}
        </View>
    );

    return (
        <View style={{ marginTop: 12 }}>
            {!readOnly && (
                <input
                    ref={fileRef}
                    type="file"
                    accept="application/pdf,image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={onPick}
                />
            )}

            {/* Upload trigger sits directly under the "MH = headcount × hours" caption. */}
            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 8, marginBottom: 8 }}>
                {!readOnly && (
                    <Pressable
                        onPress={() => fileRef.current && fileRef.current.click()}
                        title="Sub time tickets & progress reports — PDF or a photo of the paper ticket"
                        style={{ borderRadius: 8, backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', paddingTop: 6, paddingBottom: 6, paddingLeft: 10, paddingRight: 10, cursor: 'pointer', alignSelf: 'flex-start' }}
                    >
                        <Text style={{ fontSize: 12, fontWeight: '600', color: '#1D4ED8' }}>+ Sub ticket / report</Text>
                    </Pressable>
                )}
                {docs.length > 0 && (
                    <Text style={{ fontSize: 11, fontWeight: '600', color: '#64748B' }}>
                        {`${docs.length} sub doc${docs.length === 1 ? '' : 's'}`}
                    </Text>
                )}
            </View>

            {/* Headcount stat bar */}
            <View style={{ borderRadius: 10, backgroundColor: '#F1F5F9', padding: 12, flexDirection: 'row', flexWrap: 'wrap', columnGap: 18, rowGap: 8, alignItems: 'center' }}>
                {(totals || []).map((t) => (
                    <View key={t.label}>
                        <Text style={{ fontSize: 10, fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.label}</Text>
                        <Text style={{ fontSize: 16, fontWeight: '700', color: '#0F172A', fontVariantNumeric: 'tabular-nums' }}>{t.value}</Text>
                    </View>
                ))}
            </View>

            <View style={{ marginTop: pending.length || docs.length ? 10 : 0 }} />

            {/* Uploads in flight (or failed, awaiting a retry) */}
            {pending.map((item) => (
                <View key={item.id} style={{ borderRadius: 12, backgroundColor: '#fff', border: '1px solid #E2E8F0', padding: 12, marginBottom: 8, flexDirection: 'row', alignItems: 'center' }}>
                    <Thumb src={item.previewUrl} isPdf={item.file.type === 'application/pdf'} />
                    <View style={{ flex: 1, marginLeft: 10, marginRight: 8, minWidth: 0 }}>
                        <Text numberOfLines={1} style={{ fontSize: 13, fontWeight: '600', color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.file.name}</Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 6, marginTop: 4 }}>
                            {item.uploading ? <ActivityIndicator size="small" /> : null}
                            <Text style={{ fontSize: 12, color: item.failed ? '#B91C1C' : '#64748B' }}>
                                {item.uploading ? 'Uploading…' : item.failed ? 'Upload failed' : ''}
                            </Text>
                        </View>
                    </View>
                    {item.failed && (
                        <>
                            <Pressable onPress={() => setPending((p) => p.filter((x) => x.id !== item.id))} style={{ minHeight: 40, justifyContent: 'center', paddingLeft: 8, paddingRight: 8, cursor: 'pointer' }}>
                                <Text style={{ fontSize: 13, color: '#DC2626', fontWeight: '600' }}>Discard</Text>
                            </Pressable>
                            <Pressable onPress={() => uploadOne(item)} style={{ minHeight: 40, justifyContent: 'center', paddingLeft: 8, paddingRight: 8, cursor: 'pointer' }}>
                                <Text style={{ fontSize: 13, color: '#1D4ED8', fontWeight: '700' }}>Retry</Text>
                            </Pressable>
                        </>
                    )}
                </View>
            ))}

            {/* Uploaded docs: thumbnail preview + doc type / note editable in place */}
            {docs.map((doc) => {
                const meta = metaOf(doc.photoId);
                const kind = meta.docType || doc.docType;
                const isPdf = String(doc.contentType || '').includes('pdf') || /\.pdf$/i.test(doc.location || '');
                return (
                    <View key={doc.photoId} style={{ borderRadius: 12, backgroundColor: '#fff', border: '1px solid #E2E8F0', padding: 12, marginBottom: 8, flexDirection: 'row', alignItems: 'flex-start' }}>
                        {doc.sasUrl && !isPdf ? (
                            <Pressable onPress={() => setLightbox({ src: doc.sasUrl, title: doc.location || 'Document' })} style={{ flexShrink: 0, cursor: 'pointer' }}>
                                <Thumb src={doc.sasUrl} isPdf={false} />
                            </Pressable>
                        ) : (
                            <Thumb src={null} isPdf={isPdf} />
                        )}
                        <View style={{ flex: 1, marginLeft: 10, marginRight: 8, minWidth: 0, rowGap: 6 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', columnGap: 8, flexWrap: 'wrap' }}>
                                <KindTag docType={kind} />
                                <Text numberOfLines={1} style={{ flex: 1, fontSize: 12, color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 120 }}>
                                    {doc.location || 'Document'}
                                </Text>
                            </View>
                            {!readOnly && kindToggle(kind, (k) => setMeta(doc.photoId, { docType: k }), readOnly)}
                            <input
                                type="text"
                                value={meta.caption ?? doc.caption ?? ''}
                                disabled={readOnly}
                                placeholder="Subcontractor / note (e.g. ACME — crew 2)"
                                onChange={(e) => setMeta(doc.photoId, { caption: e.target.value })}
                                style={inputBase}
                            />
                        </View>
                        <View style={{ alignItems: 'flex-end', rowGap: 4 }}>
                            {doc.sasUrl && !isPdf ? (
                                <Pressable onPress={() => setLightbox({ src: doc.sasUrl, title: doc.location || 'Document' })} style={{ padding: 6, cursor: 'pointer' }}>
                                    <Text style={{ fontSize: 13, fontWeight: '600', color: '#1D4ED8' }}>View</Text>
                                </Pressable>
                            ) : doc.sasUrl ? (
                                <a href={doc.sasUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 600, color: '#1D4ED8', textDecoration: 'none', padding: 6 }}>View</a>
                            ) : null}
                            {!readOnly && (
                                <Pressable onPress={() => deleteDoc(doc)} style={{ minHeight: 32, justifyContent: 'center', paddingLeft: 6, paddingRight: 6, cursor: 'pointer' }}>
                                    <Text style={{ fontSize: 13, color: '#DC2626', fontWeight: '600' }}>Delete</Text>
                                </Pressable>
                            )}
                        </View>
                    </View>
                );
            })}

            {lightbox && <Lightbox src={lightbox.src} title={lightbox.title} onClose={() => setLightbox(null)} />}
        </View>
    );
};

export default SubDocsUpload;
